import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!user) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}小时${minutes}分钟`;
    }
    return `${minutes}分钟`;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const stats = [
    {
      label: '完成游戏',
      value: user.totalGamesCompleted,
      color: 'text-blue-400',
      emoji: '🎮',
    },
    {
      label: '简单完成',
      value: user.easyCompleted,
      color: 'text-green-400',
      emoji: '🌱',
    },
    {
      label: '中等完成',
      value: user.mediumCompleted,
      color: 'text-yellow-400',
      emoji: '⚡',
    },
    {
      label: '困难完成',
      value: user.hardCompleted,
      color: 'text-red-400',
      emoji: '🔥',
    },
    {
      label: '总游戏时长',
      value: formatTime(user.totalTimePlayed),
      color: 'text-purple-400',
      emoji: '⏱️',
    },
  ];

  return (
    <div className="py-4 sm:py-8 max-w-2xl mx-auto">
      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-5 sm:p-8 text-center mb-4 sm:mb-8"
      >
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.username}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-3 sm:mb-4 ring-4 ring-green-500/30"
          />
        ) : (
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-3xl sm:text-4xl font-bold ring-4 ring-green-500/30 text-white">
            {user.username?.[0]?.toUpperCase()}
          </div>
        )}
        
        <h1 className="text-xl sm:text-2xl font-bold mb-1">{user.username}</h1>
        {user.email && (
          <p className="text-sm sm:text-base text-gray-500">{user.email}</p>
        )}
        <p className="text-xs sm:text-sm text-gray-400 mt-2">
          加入时间：{new Date(user.createdAt).toLocaleDateString('zh-CN')}
        </p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8"
      >
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className="glass-card p-3 sm:p-4 text-center"
          >
            <div className="text-xl sm:text-2xl mb-1 sm:mb-2">{stat.emoji}</div>
            <div className={`text-xl sm:text-2xl font-bold ${stat.color}`}>
              {stat.value}
            </div>
            <div className="text-xs sm:text-sm text-gray-500">{stat.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-4 sm:p-6"
      >
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">快速操作</h2>
        <div className="space-y-2 sm:space-y-3">
          <Link
            to="/levels"
            className="flex items-center justify-between p-3 sm:p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-all touch-manipulation"
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-lg sm:text-xl">🎮</span>
              <span className="text-sm sm:text-base">继续游戏</span>
            </div>
            <span className="text-gray-400">→</span>
          </Link>
          
          <Link
            to="/leaderboard"
            className="flex items-center justify-between p-3 sm:p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-all touch-manipulation"
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-lg sm:text-xl">🏆</span>
              <span className="text-sm sm:text-base">查看排行榜</span>
            </div>
            <span className="text-gray-400">→</span>
          </Link>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between p-3 sm:p-4 rounded-lg bg-red-50 hover:bg-red-100 transition-all text-red-500 touch-manipulation"
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-lg sm:text-xl">👋</span>
              <span className="text-sm sm:text-base">退出登录</span>
            </div>
            <span>→</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
}

